import { HollowedButton, IconButton, NormalButton } from "@shared/designSystem/Button";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import { SelectionPill } from "@shared/designSystem/SelectionPill";
import {
    ColumnsContainer,
    HorizontalContainer,
    SimpleCardContainer,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer,
} from "@shared/designSystem/Containers";
import { Modal } from "@shared/designSystem/messages/Modal";
import { TextInput, TextInputRef } from "@shared/designSystem/TextInput";
import { BoldText, SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { CustomFieldsService } from "@services/CollaboratorCustomFieldsService";
import { CSSProperties, useEffect, useRef } from "react";
import { QSwitch } from "@shared/designSystem/QSwitch";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowLeftSVG } from "@assets/SVGs/Configuracion/ArrowLeft.svg";
import styles from "@styles/components/Configuracion/Novedades/ConfiguracionNovedades.module.css";

import { CustomFieldsConfigOptions, useCustomFieldsConfigScreenViewModel } from "./CustomFieldsConfigScreenViewModel";

const columnWidths = {
    name: "34%",
    type: "18%",
    required: "16%",
    active: "14%",
    actions: "18%",
};

const tableMinWidth = "980px";

/** Texts of a custom fields settings screen. All of them already localized. */
export interface CustomFieldsConfigTexts {
    title: string;
    subtitle: string;
    loadingText: string;
    emptyText: string;
    /** If set, the empty state shows this line and a centered "Crear campo" button. */
    emptyDescription?: string;
    /** If set, inactive fields show this hint under their name. */
    inactiveHint?: string;
    /** Description of the "Campo obligatorio" switch in the create modal. */
    requiredDescription: string;
    /** Description of the list options editor in the create modal. */
    listValuesDescription: string;
    /** Placeholder of the field name input in the create modal. */
    namePlaceholder: string;
    /** Placeholder of the list option input. */
    listValuePlaceholder: string;
    /** Dialog shown when deleting a field that has data (when the option is on). */
    fieldInUseTitle?: string;
    fieldInUseBody?: (fieldName: string) => string;
    fieldInUseDeactivateHint?: string;
}

interface CustomFieldTableCellProps {
    text: string;
    width: string;
    justifyContent?: CSSProperties["justifyContent"];
}

const CustomFieldHeaderCell: React.FC<CustomFieldTableCellProps> = ({
    text,
    width,
    justifyContent = "flex-start",
}) => (
    <HorizontalContainer width={width} justifyContent={justifyContent} minWidth="0">
        <Text
            text={`**${text.trim()}**`}
            markdown={true}
            color={designSystemStyles.colorTitleText}
            truncate={true}
        />
    </HorizontalContainer>
);

const CustomFieldBodyCell: React.FC<CustomFieldTableCellProps> = ({
    text,
    width,
    justifyContent = "flex-start",
}) => (
    <HorizontalContainer width={width} height="44px" justifyContent={justifyContent} minWidth="0">
        <Text text={text} truncate={true} />
    </HorizontalContainer>
);

interface CustomFieldsConfigScreenProps {
    orgID: string;
    customFieldsService: CustomFieldsService;
    options: CustomFieldsConfigOptions;
    texts: CustomFieldsConfigTexts;
}

/**
 * Settings screen for any kind of custom fields (collaborators, qontact points...).
 * Same table, create modal and inline edition for all of them.
 */
const CustomFieldsConfigScreen: React.FC<CustomFieldsConfigScreenProps> = ({
    orgID,
    customFieldsService,
    options,
    texts,
}) => {
    const navigate = useNavigate();
    const viewModel = useCustomFieldsConfigScreenViewModel({
        orgID,
        customFieldsService,
        options,
    });
    const nameInputRef = useRef<TextInputRef>(null);

    useEffect(() => {
        if (!viewModel.isCreateModalOpen) {
            return;
        }

        const timeout = window.setTimeout(() => {
            nameInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeout);
    }, [viewModel.isCreateModalOpen]);

    const isEmpty = !viewModel.isLoading && viewModel.rows.length === 0;
    // With an empty-state description, the create button moves to the center of the empty state
    const showHeaderCreateButton = !(isEmpty && texts.emptyDescription);

    return (
        <>
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapMd} margin="0">
                <HorizontalContainer
                    width="100%"
                    minHeight="132px"
                    alignment="start"
                    justifyContent="flex-start"
                    gap={designSystemStyles.gapMd}
                    paddingTop={designSystemStyles.paddingLgScreen}
                    paddingLeft={designSystemStyles.marginMd}
                    paddingRight={designSystemStyles.marginLg}
                >
                    <div className={styles['svg-container']} style={{width: '50px', height: '50px', cursor: 'pointer'}} onClick={() => {navigate(-1)}}>
                        <ArrowLeftSVG/>
                    </div>
                    <VerticalContainer width="auto" margin="0" gap={designSystemStyles.gapXxxSm}>
                        <BoldText
                            text={texts.title}
                            size="lg"
                            color={designSystemStyles.colorTitleText}
                        />
                        <Text
                            text={texts.subtitle}
                            color={designSystemStyles.colorTextSecondary}
                        />
                    </VerticalContainer>
                </HorizontalContainer>

                <HorizontalContainer
                    width="100%"
                    minHeight="44px"
                    paddingRight={designSystemStyles.marginLg}
                    justifyContent="flex-end"
                    gap={designSystemStyles.gapMd}
                >
                    {showHeaderCreateButton && (
                        <HollowedButton
                            text={"Crear".localized()}
                            onClick={viewModel.openCreateModal}
                            width="180px"
                        />
                    )}
                </HorizontalContainer>

                <VerticalContainer
                    width="100%"
                    height="calc(100% - 198px)"
                    minHeight="0"
                    paddingLeft={designSystemStyles.marginLg}
                    paddingRight={designSystemStyles.marginLg}
                    paddingBottom={designSystemStyles.marginMd}
                    margin="0"
                >
                    <SimpleCardContainer
                        height="100%"
                        padding={designSystemStyles.paddingZero}
                        backgroundColor={designSystemStyles.colorBackgroundWhite}
                        borderRadius={designSystemStyles.cornerRadiusComponent}
                        boxShadow={designSystemStyles.shadowCard}
                    >
                        <VerticalContainer height="100%" gap={designSystemStyles.gapMd}>
                            <HorizontalContainer width="100%" overflow="auto" flexShrink="0">
                                <HorizontalContainer minWidth={tableMinWidth}>
                                    <TableHeaderContainer height={designSystemStyles.sizeRowMedium}>
                                        <CustomFieldHeaderCell text={"Campo".localized()} width={columnWidths.name} />
                                        <CustomFieldHeaderCell text={"Tipo".localized()} width={columnWidths.type} />
                                        <CustomFieldHeaderCell text={"Obligatorio".localized()} width={columnWidths.required} justifyContent="center" />
                                        <CustomFieldHeaderCell text={"Activo".localized()} width={columnWidths.active} justifyContent="center" />
                                        <CustomFieldHeaderCell text={"Acciones".localized()} width={columnWidths.actions} justifyContent="center" />
                                    </TableHeaderContainer>
                                </HorizontalContainer>
                            </HorizontalContainer>

                            <VerticalContainer width="100%" height="100%" minHeight="0" scrollable={true}>
                                <VerticalContainer minWidth={tableMinWidth} minHeight="100%" gap={designSystemStyles.gapSm}>
                                    {viewModel.isLoading ? (
                                        <VerticalContainer minHeight="100%" justifyContent="center" alignment="center">
                                            <QontyLoading
                                                text={texts.loadingText}
                                                withQonty={true}
                                                darkMode={false}
                                            />
                                        </VerticalContainer>
                                    ) : isEmpty ? (
                                        <VerticalContainer minHeight="100%" justifyContent="center" alignment="center" gap={designSystemStyles.gapMd}>
                                            <QontyEmptyState
                                                text={texts.emptyText}
                                                darkMode={false}
                                                fullSize={false}
                                            />
                                            {texts.emptyDescription && (
                                                <>
                                                    <Text
                                                        text={texts.emptyDescription}
                                                        color={designSystemStyles.colorTextSecondary}
                                                        alignment="center"
                                                    />
                                                    <NormalButton
                                                        text={"Crear campo".localized()}
                                                        onClick={viewModel.openCreateModal}
                                                        width="180px"
                                                    />
                                                </>
                                            )}
                                        </VerticalContainer>
                                    ) : null}
                                    {!viewModel.isLoading && viewModel.rows.map((row) => {
                                        const isEditing = viewModel.editingFieldID === row.id;

                                        return (
                                            <TableRowContainer
                                                key={row.id}
                                                minHeight={isEditing ? undefined : designSystemStyles.sizeRowMedium}
                                                backgroundColor={isEditing ? designSystemStyles.colorTintedAction : designSystemStyles.colorBackgroundWhite}
                                                borderRadius={designSystemStyles.cornerRadiusComponent}
                                                alignment={isEditing ? "flex-start" : "center"}
                                            >
                                                <HorizontalContainer width={columnWidths.name} gap={designSystemStyles.gapSm} alignment="flex-start">
                                                    {isEditing ? (
                                                        <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                                                            <TextInput
                                                                label=""
                                                                value={viewModel.editingName}
                                                                onChange={viewModel.updateEditingName}
                                                                onEnterPress={viewModel.submitEditField}
                                                                error={viewModel.errors.editName}
                                                            />
                                                            {row.typeId === "list" ? (
                                                                <SimpleCardContainer variant="inlineSetup" boxShadow="none">
                                                                    <ListValuesEditor
                                                                        placeholder={texts.listValuePlaceholder}
                                                                        values={viewModel.editingListValues}
                                                                        inputValue={viewModel.editingListValue}
                                                                        error={viewModel.errors.editListValue}
                                                                        disabled={viewModel.isSaving}
                                                                        onInputChange={viewModel.updateEditingListValue}
                                                                        onAdd={viewModel.addEditingListValue}
                                                                        onRemove={viewModel.removeEditingListValue}
                                                                    />
                                                                </SimpleCardContainer>
                                                            ) : null}
                                                        </VerticalContainer>
                                                    ) : (
                                                        <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                                                            <Text
                                                                text={`**${(row.name ?? '').trim()}**`}
                                                                markdown={true}
                                                                color={designSystemStyles.colorTitleText}
                                                                truncate={true}
                                                            />
                                                            {row.typeId === "list" && row.values?.length ? (
                                                                <SmallText
                                                                    text={`${row.values.length} ${row.values.length === 1 ? "valor".localized() : "valores".localized()}`}
                                                                    color={designSystemStyles.colorTextSecondary}
                                                                    truncate={true}
                                                                />
                                                            ) : null}
                                                            {!row.isActive && texts.inactiveHint ? (
                                                                <SmallText
                                                                    text={texts.inactiveHint}
                                                                    color={designSystemStyles.colorTextSecondary}
                                                                    truncate={true}
                                                                />
                                                            ) : null}
                                                        </VerticalContainer>
                                                    )}
                                                </HorizontalContainer>
                                                <CustomFieldBodyCell text={row.typeLabel} width={columnWidths.type} />
                                                <HorizontalContainer width={columnWidths.required} height="44px" justifyContent="center">
                                                    {row.typeId === "boolean" ? (
                                                        <SmallText
                                                            text={"No aplica".localized()}
                                                            color={designSystemStyles.colorTextSecondary}
                                                        />
                                                    ) : (
                                                        <QSwitch
                                                            checked={row.isRequired}
                                                            onChange={() => viewModel.toggleRequiredField(row.id)}
                                                        />
                                                    )}
                                                </HorizontalContainer>
                                                <HorizontalContainer width={columnWidths.active} height="44px" justifyContent="center">
                                                    <QSwitch
                                                        checked={row.isActive}
                                                        onChange={() => viewModel.toggleField(row.id)}
                                                    />
                                                </HorizontalContainer>
                                                <HorizontalContainer width={columnWidths.actions} height="44px" justifyContent="center" gap={designSystemStyles.gapSm}>
                                                    {isEditing ? (
                                                        <>
                                                            <IconButton
                                                                iconType="check"
                                                                tooltip={"Guardar".localized()}
                                                                onClick={viewModel.submitEditField}
                                                                disabled={viewModel.isSaving}
                                                            />
                                                            <IconButton
                                                                iconType="close"
                                                                tooltip={"Cancelar".localized()}
                                                                onClick={viewModel.cancelEditingField}
                                                                disabled={viewModel.isSaving}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <IconButton
                                                                iconType="edit"
                                                                tooltip={"Editar".localized()}
                                                                onClick={() => viewModel.startEditingField(row)}
                                                                disabled={viewModel.isSaving}
                                                            />
                                                            <IconButton
                                                                iconType="delete"
                                                                tooltip={"Borrar".localized()}
                                                                onClick={() => viewModel.deleteField(row.id)}
                                                                disabled={viewModel.isSaving}
                                                            />
                                                        </>
                                                    )}
                                                </HorizontalContainer>
                                            </TableRowContainer>
                                        );
                                    })}
                                </VerticalContainer>
                            </VerticalContainer>
                        </VerticalContainer>
                    </SimpleCardContainer>
                </VerticalContainer>
            </VerticalContainer>

            <Modal
                isOpen={viewModel.isCreateModalOpen}
                onClose={viewModel.closeCreateModal}
                title={"Nuevo campo".localized()}
                width="860px"
                maxWidth="94vw"
            >
                <VerticalContainer width="100%" gap={designSystemStyles.gapLg}>
                    <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                        <Text text={`**${"Nombre del campo".localized()}**`} markdown={true} />
                        <TextInput
                            ref={nameInputRef}
                            label=""
                            value={viewModel.draft.name}
                            placeholder={texts.namePlaceholder}
                            onChange={viewModel.updateDraftName}
                            error={viewModel.errors.name}
                        />
                    </VerticalContainer>

                    {viewModel.draft.type?.id === "boolean" ? null : (
                        <SimpleCardContainer variant="inlineSetup">
                            <HorizontalContainer width="100%" justifyContent="space-between" gap={designSystemStyles.gapMd}>
                                <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                                    <Text text={"Campo obligatorio".localized()} />
                                    <SmallText
                                        text={texts.requiredDescription}
                                        color={designSystemStyles.colorTextSecondary}
                                    />
                                </VerticalContainer>
                                <QSwitch
                                    checked={viewModel.draft.isRequired}
                                    onChange={viewModel.toggleDraftRequired}
                                />
                            </HorizontalContainer>
                        </SimpleCardContainer>
                    )}

                    <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                        <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                            <Text text={"Tipo de respuesta".localized()} />
                            <SmallText
                                text={"Elegí el formato que mejor representa el dato que vas a pedir.".localized()}
                                color={designSystemStyles.colorTextSecondary}
                            />
                        </VerticalContainer>

                        <ColumnsContainer minColumnWidth="240px" gap={designSystemStyles.gapSm}>
                            {viewModel.fieldTypeOptions.map((option) => {
                                const isSelected = viewModel.draft.type?.id === option.id;

                                return (
                                    <ClickableWrapper
                                        key={option.id}
                                        onClick={() => viewModel.updateDraftType(option)}
                                        width="100%"
                                    >
                                        <SimpleCardContainer
                                            width="100%"
                                            height="100%"
                                            boxShadow="none"
                                            border={`1px solid ${isSelected ? designSystemStyles.colorMainAction : designSystemStyles.colorTextInputBorder}`}
                                            backgroundColor={isSelected ? designSystemStyles.colorTintedAction : designSystemStyles.colorBackgroundWhite}
                                        >
                                            <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                                                <Text text={`**${option.title}**`} markdown={true} />
                                                <SmallText
                                                    text={option.description}
                                                    color={designSystemStyles.colorTextSecondary}
                                                />
                                                <SmallText
                                                    text={option.example}
                                                    color={designSystemStyles.colorTextSecondary}
                                                />
                                            </VerticalContainer>
                                        </SimpleCardContainer>
                                    </ClickableWrapper>
                                );
                            })}
                        </ColumnsContainer>

                        {viewModel.errors.type ? (
                            <SmallText text={viewModel.errors.type} color={designSystemStyles.colorError} />
                        ) : null}
                    </VerticalContainer>

                    {viewModel.draft.type?.id === "list" ? (
                        <SimpleCardContainer variant="inlineSetup">
                            <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                                <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                                    <Text text={"Valores de la lista".localized()} />
                                    <SmallText
                                        text={texts.listValuesDescription}
                                        color={designSystemStyles.colorTextSecondary}
                                    />
                                </VerticalContainer>
                                <ListValuesEditor
                                    placeholder={texts.listValuePlaceholder}
                                    values={viewModel.draft.listValues}
                                    inputValue={viewModel.draft.listValue}
                                    error={viewModel.errors.listValue}
                                    disabled={viewModel.isSaving}
                                    onInputChange={viewModel.updateDraftListValue}
                                    onAdd={viewModel.addDraftListValue}
                                    onRemove={viewModel.removeDraftListValue}
                                />
                            </VerticalContainer>
                        </SimpleCardContainer>
                    ) : null}

                    <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapSm}>
                        <HollowedButton
                            text={"Cancelar".localized()}
                            onClick={viewModel.closeCreateModal}
                            width="164px"
                        />
                        <NormalButton
                            text={"Crear".localized()}
                            onClick={viewModel.submitCreate}
                            width="164px"
                            disabled={viewModel.isCreateDisabled}
                        />
                    </HorizontalContainer>
                </VerticalContainer>
            </Modal>

            <Modal
                isOpen={!!viewModel.fieldInUse}
                onClose={viewModel.closeFieldInUseDialog}
                title={texts.fieldInUseTitle ?? ""}
                width="560px"
                maxWidth="94vw"
            >
                <VerticalContainer width="100%" gap={designSystemStyles.gapLg}>
                    <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                        <Text text={texts.fieldInUseBody?.(viewModel.fieldInUse?.name ?? "") ?? ""} />
                        {viewModel.fieldInUse?.isActive && texts.fieldInUseDeactivateHint ? (
                            <Text
                                text={texts.fieldInUseDeactivateHint}
                                color={designSystemStyles.colorTextSecondary}
                            />
                        ) : null}
                    </VerticalContainer>
                    <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapSm}>
                        <HollowedButton
                            text={viewModel.fieldInUse?.isActive ? "Cancelar".localized() : "Cerrar".localized()}
                            onClick={viewModel.closeFieldInUseDialog}
                            width="164px"
                        />
                        {viewModel.fieldInUse?.isActive ? (
                            <NormalButton
                                text={"Desactivar campo".localized()}
                                onClick={viewModel.deactivateFieldInUse}
                                width="180px"
                                disabled={viewModel.isSaving}
                            />
                        ) : null}
                    </HorizontalContainer>
                </VerticalContainer>
            </Modal>
        </>
    );
};

interface ListValuesEditorProps {
    placeholder: string;
    values: Array<{ id: string; label: string }>;
    inputValue: string;
    error?: string;
    disabled?: boolean;
    onInputChange: (value: string) => void;
    onAdd: () => void;
    onRemove: (valueID: string) => void;
}

const ListValuesEditor: React.FC<ListValuesEditorProps> = ({
    placeholder,
    values,
    inputValue,
    error,
    disabled,
    onInputChange,
    onAdd,
    onRemove,
}) => {
    return (
        <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
            {values.length > 0 ? (
                <HorizontalContainer
                    width="100%"
                    gap={designSystemStyles.gapSm}
                    flexWrap="wrap"
                    maxHeight="96px"
                    overflow="auto"
                >
                    {values.map((value) => (
                        <SelectionPill
                            key={value.id}
                            label={value.label}
                            disabled={disabled}
                            onRemove={() => onRemove(value.id)}
                        />
                    ))}
                </HorizontalContainer>
            ) : null}
            <HorizontalContainer width="100%" gap={designSystemStyles.gapSm} alignment="flex-start">
                <TextInput
                    label=""
                    value={inputValue}
                    placeholder={placeholder}
                    onChange={onInputChange}
                    onEnterPress={onAdd}
                    error={error}
                />
                <NormalButton
                    text={"Agregar".localized()}
                    onClick={onAdd}
                    disabled={disabled || !inputValue.trim()}
                    width="132px"
                />
            </HorizontalContainer>
        </VerticalContainer>
    );
};

export default CustomFieldsConfigScreen;
